import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";
import { DayPicker } from "react-day-picker";

import { buttonVariants } from "@/shared/ui/Button";
import { cn } from "@/shared/lib/utils";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

type DayPickerComponents = NonNullable<CalendarProps["components"]>;
type DayPickerClassNames = NonNullable<CalendarProps["classNames"]>;

const BASE_CLASS_NAMES: DayPickerClassNames = {
  month: "space-y-4",
  months: "flex flex-col sm:flex-row space-y-4 sm:space-y-0 relative",
  month_caption: "flex justify-center pt-1 relative items-center gap-1",
  dropdowns: "flex items-center gap-2",
  dropdown_root: "relative inline-flex items-center",
  dropdown: "absolute inset-0 opacity-0 cursor-pointer z-10",
  month_grid: "w-full border-collapse space-y-1",
  caption_label: "text-sm font-medium",
  nav: "flex items-center justify-between absolute inset-x-0",
  button_previous: cn(
    buttonVariants({ variant: "outline" }),
    "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 z-10",
  ),
  button_next: cn(
    buttonVariants({ variant: "outline" }),
    "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 z-10",
  ),
  weeks: "w-full border-collapse space-y-1",
  weekdays: "flex gap-1",
  weekday: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
  week: "flex w-full mt-1 gap-1",
  day_button:
    "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20 cursor-pointer disabled:cursor-not-allowed",
  day: cn(buttonVariants({ variant: "outline" }), "h-9 w-9 p-0 font-normal aria-selected:opacity-100"),
  range_end: "day-range-end",
  selected:
    "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
  today: "bg-accent text-accent-foreground",
  outside:
    "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
  disabled: "opacity-50",
  range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
  hidden: "invisible",
};

/**
 * Defined at module scope, never inside Calendar: a component identity that
 * changes on every render makes React unmount and rebuild the whole DayPicker
 * subtree — hundreds of caption options and day buttons — instead of updating
 * it. See __tests__/Calendar.reconciliation.test.tsx.
 */
const CalendarChevron: DayPickerComponents["Chevron"] = ({ orientation, ...props }) =>
  orientation === "left"
    ? <ChevronLeft {...props} className="h-4 w-4" />
    : <ChevronRight {...props} className="h-4 w-4" />;

const CalendarDropdownNav: DayPickerComponents["DropdownNav"] = ({ children, ...props }) => (
  <div {...props} className="flex items-center gap-1">
    {children}
  </div>
);

const CalendarDropdown: DayPickerComponents["Dropdown"] = ({
  value, options, onChange, ...props
}) => (
  <div className="relative inline-flex items-center">
    <select
      {...props}
      value={value}
      onChange={onChange}
      className="appearance-none bg-transparent pr-5 pl-1.5 py-0.5 text-sm font-medium rounded-md border border-input hover:bg-accent cursor-pointer focus:outline-none focus:ring-1 focus:ring-ring"
    >
      {options?.map((option) => (
        <option key={option.value} value={option.value} disabled={option.disabled}>
          {option.label}
        </option>
      ))}
    </select>
    <ChevronDown className="pointer-events-none absolute right-1 h-3 w-3 text-muted-foreground" />
  </div>
);

const BASE_COMPONENTS: DayPickerComponents = {
  Chevron: CalendarChevron,
  DropdownNav: CalendarDropdownNav,
  Dropdown: CalendarDropdown,
};

function Calendar({
  className, classNames, showOutsideDays = true, components: userComponents, ...props
}: CalendarProps) {
  const mergedClassNames = React.useMemo(
    () => ({ ...BASE_CLASS_NAMES, ...classNames }),
    [classNames],
  );
  const mergedComponents = React.useMemo(
    () => ({ ...BASE_COMPONENTS, ...userComponents }),
    [userComponents],
  );

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={mergedClassNames}
      components={mergedComponents}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
